import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@tectonic/errors';
import { recoverEmailPassword } from '@tectonic/remix-client-network';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { usePageFragment } from '../../../hooks';

import type {
  ElemasonAuthPasswordRecoveryFormWidget,
  UserEmailPasswordRecoveryPayload,
} from '@tectonic/types';
import type { AuthPasswordRecoveryFormValue } from '../../../components';

const recoverEmailPasswordMutationFn = async (
  payload: UserEmailPasswordRecoveryPayload
) => {
  const response = await recoverEmailPassword(payload);
  if (response.error) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw response.error;
  }
  return response.data;
};

const usePasswordRecoveryForm = (
  widget: ElemasonAuthPasswordRecoveryFormWidget
) => {
  const wData = widget.data!;

  const { showToast } = useToast();

  const fragment = usePageFragment(wData.recoverySuccessFragment);

  const {
    isPending: isLoading,
    isSuccess,
    mutateAsync: recoverPassword,
  } = useMutation({
    mutationFn: recoverEmailPasswordMutationFn,
    mutationKey: [],
  });

  const onSubmit = async (value: AuthPasswordRecoveryFormValue) => {
    try {
      await recoverPassword(value);
    } catch (error) {
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  return { isLoading, onSubmit, isSuccess, fragment };
};

export { usePasswordRecoveryForm };
